import React from 'react';
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

// --------------------------------------------------------------------------
// Formatters (self-contained)
// --------------------------------------------------------------------------
export const usd0 = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `$${Math.round(Number(v)).toLocaleString()}`;
export const num = (v) =>
  v == null || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
export const pct = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`;

const DIRECTION_TONE = {
  increase: 'success',
  pull_back: 'error',
  at_capacity: 'info',
  saturated: 'info', // legacy payloads
  hold: 'default',
};
const DIRECTION_LABEL = {
  increase: 'Scale up',
  pull_back: 'Pull back',
  at_capacity: 'At capacity',
  saturated: 'At capacity', // legacy payloads
  hold: 'On track',
};

const TREND_META = {
  improving: { arrow: '▲', color: 'success.main', label: 'Improving' },
  declining: { arrow: '▼', color: 'error.main', label: 'Softening' },
};

// Recent-window trend vs the fit window ("recent 30d CoM 25% vs 19%").
export function TrendChip({ trend }) {
  const meta = TREND_META[trend?.direction];
  if (!meta) return null;
  const basis = trend.basis === 'com'
    ? `CoM ${pct(trend.recent_com)} vs ${pct(trend.window_com)}`
    : trend.basis === 'cpl'
      ? `CPL ${usd0(trend.recent_cpl)} vs ${usd0(trend.window_cpl)}`
      : '';
  return (
    <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {meta.arrow} {meta.label} last {trend.recent_days}d{basis ? ` · ${basis}` : ''}
    </Typography>
  );
}

// Ranked "what's limiting" / "what's working" factor lists from the
// recommendation's funnel decomposition.
export function DriverList({ title, items, tone, max = 3 }) {
  if (!items || items.length === 0) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: `${tone}.main` }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ pl: 2, my: 0 }}>
        {items.slice(0, max).map((d) => (
          <Typography key={d.key} component="li" variant="caption" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{d.label}</Box>
            {d.detail
              ? ` — ${d.detail}`
              : d.segment_value != null && d.reference_value != null
                ? ` — ${fmtFactor(d.key, d.segment_value)} vs ${fmtFactor(d.key, d.reference_value)} ${
                    d.reference_source === 'vertical_peers' ? 'vertical norm' : 'your vertical avg'
                  }`
                : ''}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

const fmtFactor = (key, v) =>
  key === 'cpl' || key === 'avg_job_size' ? usd0(v) : pct(v);

// Blended-portfolio banner: the goal is a blended target across markets, so
// say where the blend sits and where the recommended plan takes it.
export function PortfolioBanner({ portfolio, goalSource }) {
  if (!portfolio || portfolio.blended_com == null || !portfolio.goal_com) return null;
  const over = portfolio.stance === 'over_goal';
  const goalTxt = `${pct(portfolio.goal_com)}${goalSource === 'default' ? ' (standard target)' : ' goal'}`;
  const projected = portfolio.projected_blended_com;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, mt: 1.5, borderRadius: 2,
        bgcolor: over ? 'rgba(237, 108, 2, 0.08)' : 'rgba(46, 125, 50, 0.08)',
        border: '1px solid',
        borderColor: over ? 'warning.light' : 'success.light',
      }}
    >
      <Typography variant="body2">
        Blended cost of marketing <b>{pct(portfolio.blended_com)}</b> vs {goalTxt} — individual
        markets run above and below it; what matters is the blend.
        {projected != null && Math.abs(projected - portfolio.blended_com) >= 0.005 && (
          <> Applying the recommendations below moves it to ~<b>{pct(projected)}</b>.</>
        )}
      </Typography>
    </Paper>
  );
}

// --------------------------------------------------------------------------
// ComGauge — cost of marketing vs goal, colored by efficiency band
// --------------------------------------------------------------------------
export function ComGauge({ com, goal, size = 'small' }) {
  if (com == null) {
    return <Chip size={size} variant="outlined" label="CoM —" />;
  }
  let color = 'default';
  if (goal) {
    const ratio = com / goal;
    color = ratio <= 0.85 ? 'success' : ratio <= 1.1 ? 'warning' : 'error';
  }
  return <Chip size={size} color={color} label={`${pct(com)}${goal ? ` / ${pct(goal)}` : ''}`} />;
}

// --------------------------------------------------------------------------
// ScenarioSummary — compact client-total cards (get-started projections style).
// Each card shows the expected value big + the low–high range as subtext.
// --------------------------------------------------------------------------
export function ScenarioSummary({ scenarios, goalCom }) {
  if (!scenarios) return null;
  const exp = scenarios.expected || {};
  const lo = scenarios.low || {};
  const hi = scenarios.high || {};
  const cards = [
    { label: 'Net revenue', val: usd0(exp.net_revenue), sub: `${usd0(lo.net_revenue)} – ${usd0(hi.net_revenue)}`, big: true },
    { label: 'Leads', val: num(exp.leads), sub: `${num(lo.leads)} – ${num(hi.leads)}` },
    { label: 'Appointments', val: num(exp.appointments), sub: `${num(lo.appointments)} – ${num(hi.appointments)}` },
    { label: 'Booked', val: num(exp.booked), sub: `${num(lo.booked)} – ${num(hi.booked)}` },
    { label: 'Cost of marketing', val: pct(exp.com), sub: goalCom ? `goal ${pct(goalCom)}` : `${pct(hi.com)} – ${pct(lo.com)}` },
    { label: 'Spend', val: usd0(exp.spend), sub: '' },
  ];
  return (
    <Grid container spacing={1.5}>
      {cards.map((c) => (
        <Grid item xs={6} sm={4} md={2} key={c.label}>
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', height: '100%' }}>
            <Typography color="text.secondary" variant="caption">{c.label}</Typography>
            <Typography variant={c.big ? 'h5' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {c.val}
            </Typography>
            {c.sub ? (
              <Typography color="text.secondary" variant="caption">{c.sub}</Typography>
            ) : null}
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// --------------------------------------------------------------------------
// MonthToDateView — "This month vs forecast": the snapshot we predicted before
// the month began, month-to-date actuals, and a straight run-rate projection.
// Prediction graded in public — the credibility loop.
// --------------------------------------------------------------------------
const MTD_ROWS = [
  { key: 'net_revenue', label: 'Net revenue', fmt: usd0, kind: 'volume' },
  { key: 'leads', label: 'Leads', fmt: num, kind: 'volume' },
  { key: 'appointments', label: 'Appointments', fmt: num, kind: 'volume' },
  { key: 'booked', label: 'Booked', fmt: num, kind: 'volume' },
  { key: 'spend', label: 'Spend', fmt: usd0, kind: 'spend' },
  { key: 'com', label: 'Cost of marketing', fmt: pct, kind: 'com' },
];

function mtdStatus(row, projected, exp, lo, hi) {
  const p = projected?.[row.key];
  if (p == null || exp?.[row.key] == null) return null;
  if (row.kind === 'com') {
    // Lower is better; the "low" scenario carries the WORSE (higher) CoM.
    if (p <= exp.com) return { label: 'Better than forecast', color: 'success' };
    if (lo?.com != null && p <= lo.com) return { label: 'Within range', color: 'default' };
    return { label: 'Worse than range', color: 'error' };
  }
  if (row.kind === 'spend') {
    if (hi?.spend != null && p > hi.spend * 1.05) return { label: 'Over plan', color: 'warning' };
    if (lo?.spend != null && p < lo.spend * 0.95) return { label: 'Under-delivering', color: 'warning' };
    return { label: 'On plan', color: 'default' };
  }
  if (p >= exp[row.key]) return { label: 'Ahead of forecast', color: 'success' };
  if (lo?.[row.key] != null && p >= lo[row.key]) return { label: 'Within range', color: 'default' };
  return { label: 'Behind range', color: 'error' };
}

export function MonthToDateView({ data }) {
  if (!data) return null;
  const f = data.forecast || {};
  const exp = f.expected || {};
  const lo = f.low || {};
  const hi = f.high || {};
  const act = data.actual_mtd || {};
  const proj = data.projected_month || {};
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>This month vs forecast</Typography>
        <Typography variant="caption" color="text.secondary">
          day {data.days_elapsed} of {data.days_in_month}
        </Typography>
      </Stack>
      {data.reconstructed && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'warning.main' }}>
          No forecast snapshot predates this month yet — the comparison uses a reconstruction as of
          the month start. From next month on, this grades the real stored prediction.
        </Typography>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
              <TableCell>Metric</TableCell>
              <TableCell align="right">Forecast</TableCell>
              <TableCell align="right">Actual so far</TableCell>
              <TableCell align="right">Projected month</TableCell>
              <TableCell>vs forecast</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MTD_ROWS.map((row) => {
              const st = mtdStatus(row, proj, exp, lo, hi);
              const band = row.kind === 'com'
                ? (lo[row.key] != null ? `${row.fmt(hi[row.key])} – ${row.fmt(lo[row.key])}` : '')
                : (lo[row.key] != null ? `${row.fmt(lo[row.key])} – ${row.fmt(hi[row.key])}` : '');
              return (
                <TableRow key={row.key}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{row.fmt(exp[row.key])}</Typography>
                    {band && (
                      <Typography variant="caption" color="text.secondary">{band}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{row.fmt(act[row.key])}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{row.fmt(proj[row.key])}</Typography>
                  </TableCell>
                  <TableCell>
                    {st ? <Chip size="small" color={st.color} label={st.label} /> : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Projected month = straight run rate of the month so far. Backend revenue often reports
        late, so revenue and CoM usually look worse mid-month than they land.
      </Typography>
    </Box>
  );
}

// --------------------------------------------------------------------------
// DiminishingReturnsChart — projected leads across the spend range, with the
// current operating point marked (concave when the elasticity fit found k>0).
// --------------------------------------------------------------------------
export function DiminishingReturnsChart({ segment, daysInMonth, appliedBudget, height = 160 }) {
  const curve = segment?.curve;
  if (!curve || !curve.cpl_at_base) return null;

  // The market ceiling can sit below the current budget — the curve must still
  // cover the operating point.
  const floor = Math.min(segment.spend_floor ?? segment.base_budget * 0.5, segment.base_budget);
  const ceiling = Math.max(segment.spend_ceiling ?? segment.base_budget * 2, segment.base_budget);
  const span = Math.max(ceiling - floor, 1);
  const N = 24;
  const data = [];
  for (let i = 0; i <= N; i += 1) {
    const spend = floor + (span * i) / N;
    const daily = daysInMonth ? spend / daysInMonth : spend / 30;
    let cpl = curve.cpl_at_base;
    if (curve.k && curve.spend_base_daily && daily > 0) {
      cpl = curve.cpl_at_base * (1 + curve.k * Math.log(daily / curve.spend_base_daily));
    }
    const leads = cpl > 0 ? spend / cpl : 0;
    data.push({ spend: Math.round(spend), leads: Math.round(leads) });
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <XAxis
          dataKey="spend"
          tick={{ fontSize: 10 }}
          minTickGap={24}
          tickFormatter={(v) => (v >= 10000 ? `$${Math.round(v / 1000)}k` : `$${(v / 1000).toFixed(1)}k`)}
        />
        <YAxis tick={{ fontSize: 10 }} width={28} />
        <RechartsTooltip
          formatter={(value, name) => (name === 'leads' ? [num(value), 'Leads'] : [usd0(value), name])}
          labelFormatter={(v) => `Spend ${usd0(v)}`}
        />
        <Line type="monotone" dataKey="leads" stroke="#1976d2" strokeWidth={2} dot={false} isAnimationActive={false} />
        {appliedBudget != null && (
          <ReferenceLine x={Math.round(appliedBudget)} stroke="#9c27b0" strokeDasharray="4 3" />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// --------------------------------------------------------------------------
// SegmentTable — scannable one-row-per-segment view, sorted exceptions-first.
// Expand a row to reveal its recommendation detail, budget slider, and curve.
// Stays digestible for clients with many segments.
// --------------------------------------------------------------------------
function SegmentDetail({ segment, rec, value, goalCom, daysInMonth, onSlider }) {
  const direction = rec?.direction || 'hold';
  const tone = DIRECTION_TONE[direction] || 'default';
  // Bounds must always include the current budget — a market ceiling below it
  // means "trim", not "the status quo is unmodelable".
  const floor = Math.max(
    0,
    Math.round(Math.min(segment.spend_floor ?? segment.base_budget * 0.5, segment.base_budget))
  );
  const ceiling = Math.round(
    Math.max(segment.spend_ceiling ?? segment.base_budget * 2, segment.base_budget)
  );
  const step = Math.max(50, Math.round((ceiling - floor) / 100));
  const hasDrivers = (rec?.constraints || []).length > 0 || (rec?.enablers || []).length > 0;

  return (
    <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
      {rec && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{rec.headline}</Typography>
            <TrendChip trend={rec.trend} />
          </Stack>
          {direction !== 'hold' && Math.round(rec.recommended_budget) !== Math.round(rec.current_budget) && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" label={`Now ${usd0(rec.current_budget)}/mo`} />
              <Typography color="text.secondary">→</Typography>
              <Chip size="small" color={tone} label={`Suggest ${usd0(rec.recommended_budget)}/mo`} />
              {direction === 'increase' && rec.scale_tier === 'early_signal' && (
                <Chip size="small" variant="outlined" color="info" label="Early signal — stepped" />
              )}
            </Stack>
          )}
          <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            {(rec.why || []).map((w, i) => (
              <Typography key={i} component="li" variant="caption" color="text.secondary">{w}</Typography>
            ))}
          </Box>
          {hasDrivers && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <DriverList title="What's limiting this market" items={rec.constraints} tone="warning" />
              </Grid>
              <Grid item xs={12} md={6}>
                <DriverList title="What's working" items={rec.enablers} tone="success" />
              </Grid>
            </Grid>
          )}
          {rec.unlock?.detail && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
              💡 {rec.unlock.detail}
            </Typography>
          )}
          {rec.potential && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Market potential: ~{usd0(rec.potential.low)}
              {rec.potential.high !== rec.potential.low ? `–${usd0(rec.potential.high)}` : ''}/mo · directional
              {rec.potential.months_to_reach
                ? ` · ~${rec.potential.months_to_reach} monthly steps to reach`
                : ''}
            </Typography>
          )}
          {rec.ramp?.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Ramp: {usd0(rec.current_budget)} → {rec.ramp.map((r) => usd0(r.budget)).join(' → ')}
            </Typography>
          )}
          {rec.volume_option?.detail && (
            <Typography
              variant="caption"
              sx={{
                display: 'block', mt: 1, pl: 1, fontStyle: 'italic',
                color: 'text.secondary', borderLeft: '2px solid', borderColor: 'divider',
              }}
            >
              {rec.volume_option.detail}
            </Typography>
          )}
        </Box>
      )}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary">
            Model a different budget for this segment
          </Typography>
          <Slider
            value={Math.min(Math.max(value, floor), ceiling)}
            min={floor}
            max={ceiling}
            step={step}
            onChange={(_e, v) => onSlider(segment.budget_id, Array.isArray(v) ? v[0] : v)}
            valueLabelDisplay="auto"
            valueLabelFormat={usd0}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">{usd0(floor)}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{usd0(value)}/mo</Typography>
            <Typography variant="caption" color="text.secondary">{usd0(ceiling)}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <DiminishingReturnsChart segment={segment} daysInMonth={daysInMonth} appliedBudget={value} />
        </Grid>
      </Grid>
    </Box>
  );
}

export function SegmentTable({
  segments, recById, outputsById, budgets, goalCom, daysInMonth, expanded, onToggle, onSlider,
}) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
            <TableCell sx={{ width: 36 }} />
            <TableCell>Segment</TableCell>
            <TableCell>Recommendation</TableCell>
            <TableCell align="right">Budget</TableCell>
            <TableCell align="right">Net revenue (next month)</TableCell>
            <TableCell align="right">CoM</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {segments.map((seg) => {
            const id = seg.budget_id;
            const rec = recById[id];
            const out = outputsById[id] || seg.outputs || {};
            const exp = out.expected || {};
            const lo = out.low || {};
            const hi = out.high || {};
            const direction = rec?.direction || 'hold';
            const tone = DIRECTION_TONE[direction] || 'default';
            const isOpen = expanded.has(id);
            const value = budgets[id] ?? seg.base_budget;
            return (
              <React.Fragment key={id}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer', '& > td': { borderBottom: isOpen ? 'none' : undefined } }}
                  onClick={() => onToggle(id)}
                >
                  <TableCell sx={{ pr: 0 }}>
                    <IconButton size="small">
                      {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{seg.scope_label || 'Segment'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {seg.scope_mode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={tone} label={DIRECTION_LABEL[direction] || direction} />
                  </TableCell>
                  <TableCell align="right">{usd0(value)}/mo</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}>{usd0(exp.net_revenue)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {usd0(lo.net_revenue)} – {usd0(hi.net_revenue)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <ComGauge com={exp.com} goal={goalCom} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <SegmentDetail
                        segment={seg}
                        rec={rec}
                        value={value}
                        goalCom={goalCom}
                        daysInMonth={daysInMonth}
                        onSlider={onSlider}
                      />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
